import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle } from 'lucide-react';

const AdminProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdminAuth = () => {
      try {
        const adminSession = localStorage.getItem('adminSession');
        
        if (!adminSession) {
          // No admin session found, redirect to login
          navigate('/admin-login');
          return;
        }

        const session = JSON.parse(adminSession);
        
        // Check if session is valid
        if (!session.isAuthenticated || !session.username || !session.loginTime) {
          // Invalid session, redirect to login
          localStorage.removeItem('adminSession');
          navigate('/admin-login');
          return;
        }

        // Check if session is not expired (24 hours)
        const loginTime = new Date(session.loginTime);
        const now = new Date();
        const hoursDiff = (now - loginTime) / (1000 * 60 * 60);
        
        if (hoursDiff > 24) {
          // Session expired, redirect to login
          localStorage.removeItem('adminSession');
          navigate('/admin-login');
          return;
        }

        // Session is valid
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error checking admin authentication:', error);
        // Error occurred, redirect to login
        localStorage.removeItem('adminSession');
        navigate('/admin-login');
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminAuth();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-purple-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Verifying Admin Access</h2>
          <p className="text-gray-600">Please wait while we verify your credentials...</p>
          <div className="mt-4">
            <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You need to be logged in as an admin to access this page.</p>
          <button
            onClick={() => navigate('/admin-login')}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go to Admin Login
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default AdminProtectedRoute;
